<template>
  <Dialog 
    :visible="visible" 
    @update:visible="$emit('close')"
    :header="title"
    :modal="true"
    :style="{ width: '90vw', maxWidth: '800px' }"
    class="movie-details-modal"
  >
    <div class="movie-details-content">
      <!-- Trailer Section -->
      <div v-if="metadata.trailer && metadata.trailer.content" class="trailer-section">
        <h3>Trailer</h3>
        <div v-if="isImdbVideo(metadata.trailer.content)" class="trailer-link-container">
          <p class="trailer-info">Click the link below to watch:</p>
          <Button 
            :label="'Watch on IMDb'" 
            icon="pi pi-external-link"
            class="p-button-lg imdb-trailer-button"
            @click="openTrailerLink(metadata.trailer.content)"
          />
        </div>
        <div v-else class="trailer-container">
          <iframe
            :src="getEmbedUrl(metadata.trailer.content)"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      </div>
      
      <!-- Movie Information -->
      <div class="movie-info-grid">
        <!-- Director -->
        <div v-if="metadata.director && metadata.director.content" class="info-item">
          <label>Director</label>
          <div class="info-value">{{ metadata.director.content }}</div>
        </div>
        
        <!-- Actors -->
        <div v-if="metadata.actors && metadata.actors.content && metadata.actors.content.length > 0" class="info-item full-width">
          <label>Cast</label>
          <div class="info-value actors-list">
            <Tag 
              v-for="(actor, index) in metadata.actors.content" 
              :key="index"
              :value="actor"
              class="actor-tag"
            />
          </div>
        </div>
        
        <!-- Genres -->
        <div v-if="metadata.genres && metadata.genres.content && metadata.genres.content.length > 0" class="info-item">
          <label>Genres</label>
          <div class="info-value">{{ metadata.genres.content.join(', ') }}</div>
        </div>
        
        <!-- Duration -->
        <div v-if="metadata.duration_minutes && metadata.duration_minutes.content" class="info-item">
          <label>Duration</label>
          <div class="info-value">{{ metadata.duration_minutes.content }} minutes</div>
        </div>
        
        <!-- Release Date -->
        <div v-if="metadata.release_date && metadata.release_date.content" class="info-item">
          <label>Release Date</label>
          <div class="info-value">{{ formatReleaseDate(metadata.release_date.content) }}</div>
        </div>
        
        <!-- IMDB Rating -->
        <div v-if="metadata.imdb_rating && metadata.imdb_rating.content" class="info-item">
          <label>IMDb Rating</label>
          <div class="info-value">
            <img src="/imdb-logo.svg" alt="IMDb" class="imdb-logo" />
            {{ metadata.imdb_rating.content }}
          </div>
        </div>
        
        <!-- Rotten Tomatoes Score -->
        <div v-if="metadata.rotten_tomatoes_score && metadata.rotten_tomatoes_score.content" class="info-item">
          <label>Rotten Tomatoes</label>
          <div class="info-value rating-value">
            <span class="tomato-icon">🍅</span>
            {{ metadata.rotten_tomatoes_score.content }}%
          </div>
        </div>
        
        <!-- User Rating -->
        <div v-if="!metadata.imdb_rating && metadata.rating && metadata.rating.content" class="info-item">
          <label>User Rating</label>
          <div class="info-value rating-value">
            <ThumbsUp :size="16" />
            {{ formatUserRating(metadata.rating.content) }}
            <span v-if="metadata.rating_count && metadata.rating_count.content" class="rating-count">
              ({{ metadata.rating_count.content.toLocaleString() }} ratings)
            </span>
          </div>
        </div>
      </div>

      <!-- Where to watch (lazy chunk; never mounts on public/embed renders — showStreaming stays false there) -->
      <StreamingWhereToWatch v-if="showStreaming && linkId" :link-id="linkId" :metadata="metadata" />
    </div>

    <template #footer>
      <Button 
        label="Close" 
        icon="pi pi-times" 
        @click="$emit('close')"
        class="p-button-text"
      />
    </template>
  </Dialog>
</template>

<script>
import { computed, defineAsyncComponent } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { Star, ThumbsUp } from 'lucide-vue-next'
import { formatDateLong } from '../utils/metadataFormatters.js'

// Deliberately a LAZY import: StreamingWhereToWatch uses i18n, and this file is part of the
// i18n-free embed bundle closure (tests/embedNoI18n.test.js). The chunk only loads when the
// section actually renders, which never happens on public/embed views (showStreaming=false).
const StreamingWhereToWatch = defineAsyncComponent(() => import('./StreamingWhereToWatch.vue'))

export default {
  name: 'MovieDetailsModal',
  components: {
    Dialog,
    Button,
    Tag,
    Star,
    ThumbsUp,
    StreamingWhereToWatch
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    metadata: {
      type: Object,
      required: true
    },
    linkId: {
      type: [Number, String],
      default: null
    },
    // Fail-closed: only the logged-in app passes true (never public/embed renders).
    showStreaming: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props) {
    const isImdbVideo = (url) => {
      if (!url) return false
      return url.startsWith('https://www.imdb.com/video/')
    }
    
    const openTrailerLink = (url) => {
      if (url) {
        window.open(url, '_blank')
      }
    }
    
    const getEmbedUrl = (url) => {
      if (!url) return ''
      
      // Handle YouTube URLs
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = ''
        
        if (url.includes('youtube.com/watch')) {
          const urlParams = new URLSearchParams(url.split('?')[1])
          videoId = urlParams.get('v')
        } else if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1].split('?')[0]
        }
        
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`
        }
      }
      
      // For other URLs, return as-is
      return url
    }
    
    const formatReleaseDate = (dateStr) => {
      return formatDateLong(dateStr)
    }
    
    const formatUserRating = (rating) => {
      if (typeof rating !== 'number') return rating
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      return rating.toFixed(1)
    }
    
    return {
      isImdbVideo,
      openTrailerLink,
      getEmbedUrl,
      formatReleaseDate,
      formatUserRating
    }
  }
}
</script>

<style scoped>
.movie-details-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.trailer-section h3 {
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  color: var(--text-primary);
  font-weight: 600;
}

.trailer-container {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.trailer-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.trailer-link-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 2px dashed var(--border-color);
}

.trailer-info {
  margin: 0;
  color: var(--text-secondary);
  text-align: center;
  font-size: 0.95rem;
}

.imdb-trailer-button {
  background: linear-gradient(135deg, #cda801 0%, #b89401 100%) !important;
  border: none !important;
  font-weight: 600;
  padding: 0.75rem 1.5rem !important;
}

.imdb-trailer-button:hover {
  background: linear-gradient(135deg, #b89401 0%, #a48001 100%) !important;
  box-shadow: 0 4px 12px rgba(205, 168, 1, 0.4);
  transform: translateY(-1px);
}

.movie-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.25rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  color: var(--text-primary);
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-value .imdb-logo {
  height: 16px;
  width: auto;
}

.info-value.rating-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #cda801;
  font-weight: 600;
}

.rating-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  margin-left: 0.25rem;
}

.actors-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.actor-tag {
  background: linear-gradient(135deg, rgba(205, 168, 1, 0.1) 0%, rgba(205, 168, 1, 0.05) 100%);
  color: var(--text-primary);
  border: 1px solid rgba(205, 168, 1, 0.3);
  font-weight: 500;
}

.tomato-icon {
  font-size: 1.2rem;
  line-height: 1;
}

/* Dark theme adjustments. NOT `:deep(:root...)` — that compiles to
   `[data-v] :root...`, which can never match. A scoped `:root.dark-theme .x`
   rule reaches these slot elements even though the Dialog teleports them. */
:root.dark-theme .actor-tag {
  background: linear-gradient(135deg, rgba(205, 168, 1, 0.15) 0%, rgba(205, 168, 1, 0.08) 100%);
  border-color: rgba(205, 168, 1, 0.4);
}

:root.dark-theme .trailer-container {
  background: #1a1a1a;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .movie-info-grid {
    grid-template-columns: 1fr;
  }
  
  :deep(.p-dialog) {
    width: 95vw !important;
    max-width: 95vw !important;
  }
}
</style>
